import React, { useState, useEffect, useRef } from 'react';
import { Search, Menu, User, Settings, LogOut, Star, FileText, CheckCircle, LogIn, UserPlus } from 'lucide-react';
import SearchResults from './SearchResults';
import { useNavigate } from 'react-router-dom';

function App() {
    // Simplified state management - single source of truth for search
    const [searchInput, setSearchInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showAppsMenu, setShowAppsMenu] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [currentSearchQuery, setCurrentSearchQuery] = useState('');
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

    const searchInputRef = useRef(null);
    const userMenuRef = useRef(null);
    const appsMenuRef = useRef(null);

    // API configuration
    const API_BASE_URL = 'https://search.mg.formafusion.io';
    const SUGGESTIONS_ENDPOINT = '/api/key-suggestion';
    const USER_INFO_ENDPOINT = '/api/user-info';

    const getUserInfo = async () => {
        if (!isLoggedIn) {
            setUserInfo(null);
            return null;
        }

        try {
            const response = await fetch(`${API_BASE_URL}${USER_INFO_ENDPOINT}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const userData = await response.json();
            setUserInfo(userData);
            return userData;
        } catch (error) {
            console.error('Error fetching user info:', error);
            setUserInfo(null);
            return null;
        }
    };

    // Initialize authentication state from localStorage
    useEffect(() => {
        const authToken = localStorage.getItem('authToken');
        const storedUserInfo = localStorage.getItem('userInfo');

        if (authToken) {
            setIsLoggedIn(true);
            if (storedUserInfo) {
                try {
                    setUserInfo(JSON.parse(storedUserInfo));
                } catch (error) {
                    console.error('Error parsing stored user info:', error);
                }
            }
            // Fetch fresh user info
            getUserInfo();
        }
    }, []);

    // Update localStorage when user info changes
    useEffect(() => {
        if (userInfo) {
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
        } else {
            localStorage.removeItem('userInfo');
        }
    }, [userInfo]);

    // Function to fetch suggestions from Laravel backend
    const fetchSuggestions = async (key) => {
        // Check if user is logged in before making API call
        if (!isLoggedIn) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        if (!key || key.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setIsLoadingSuggestions(true);
        const idCustomer = localStorage.getItem('idCustomer');

        try {
            const response = await fetch(`${API_BASE_URL}${SUGGESTIONS_ENDPOINT}?key=${encodeURIComponent(key)}&idCustomer=${idCustomer}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const transformedSuggestions = data.map((item, index) => ({
                id: index,
                text: item.context,
                subContext: item.subContext,
                image: item.image,
                type: 'suggestion'
            }));

            setSuggestions(transformedSuggestions);
            setShowSuggestions(transformedSuggestions.length > 0);

        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsLoadingSuggestions(false);
        }
    };

    // Debounce function
    const debounce = (func, wait) => {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    };

    const debouncedFetchSuggestions = debounce(fetchSuggestions, 300);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
            if (appsMenuRef.current && !appsMenuRef.current.contains(event.target)) {
                setShowAppsMenu(false);
            }
            // Close suggestions when clicking outside
            if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fixed search input handler
    const handleSearchInput = (value) => {
        setSearchInput(value);

        if (value.length >= 2) {
            debouncedFetchSuggestions(value);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    // Fixed search function
    const performSearch = (query = null) => {
        const searchQuery = query || searchInput;

        if (!searchQuery || !searchQuery.trim()) {
            console.warn('Empty search query');
            return;
        }

        // Check if user is logged in before allowing search
        if (!isLoggedIn) {
            // You could show a modal or redirect to login page here
            alert('Veuillez vous connecter pour effectuer une recherche');
            return;
        }

        // Set the current search query for results
        setCurrentSearchQuery(searchQuery);

        // Show search results
        setShowSearchResults(true);

        // Update search input to match what was searched
        setSearchInput(searchQuery);

        // Hide suggestions
        setShowSuggestions(false);
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            performSearch();
        }
    };

    const navigate = useNavigate();

    // Handle suggestion selection
    const handleSuggestionClick = (suggestionText) => {
        // Check authentication before allowing suggestion click
        if (!isLoggedIn) {
            alert('Veuillez vous connecter pour effectuer une recherche');
            return;
        }

        setSearchInput(suggestionText);
        performSearch(suggestionText);
    };

    const goBackToSearch = () => {
        setShowSearchResults(false);
        setCurrentSearchQuery('');
        setSearchInput('');
        setShowSuggestions(false);
    };

    const handleLogout = () => {
        // Clear all authentication data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('idCustomer');
        localStorage.removeItem('type_customer');

        setIsLoggedIn(false);
        setUserInfo(null);
        setShowUserMenu(false);
        setShowSearchResults(false);
        setCurrentSearchQuery('');
        setSearchInput('');
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const handleLogin = () => {
        navigate('/login');
    };

    const handleRegister = () => {
        navigate('/register');
    };

    // Render suggestion item
    const renderSuggestion = (suggestion) => (
        <button
            key={suggestion.id}
            onClick={() => handleSuggestionClick(suggestion.text)}
            className="w-full p-4 text-left transition-all duration-200 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 group"
        >
            <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-10 h-10 overflow-hidden rounded-full">
                    <img
                        src={suggestion.image}
                        alt={suggestion.text}
                        className="object-cover w-full h-full"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
                <div className="flex flex-col flex-1 space-y-1">
                    <p className="font-semibold text-gray-900 sm:text-lg">
                        {suggestion.text}
                    </p>
                    <p className="text-sm text-gray-600">
                        {suggestion.subContext}
                    </p>
                </div>
            </div>
        </button>
    );

    // Render suggestions dropdown
    const renderSuggestions = () => {
        if (!showSuggestions) return null;

        return (
            <div className="absolute left-0 right-0 z-30 mt-2 border border-gray-100 shadow-2xl top-full rounded-xl bg-white/95 backdrop-blur-md">
                <div className="p-2 overflow-y-auto max-h-60">
                    {isLoadingSuggestions ? (
                        <div className="flex items-center justify-center p-4">
                            <div className="w-5 h-5 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                            <span className="ml-2 text-sm text-gray-600">Recherche...</span>
                        </div>
                    ) : suggestions.length > 0 ? (
                        suggestions.map(renderSuggestion)
                    ) : (
                        <div className="p-3 text-sm text-center text-gray-500">
                            Aucune suggestion trouvée
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render authentication buttons (when not logged in)
    const renderAuthButtons = () => (
        <div className="flex items-center space-x-2 md:space-x-3">
            <button
                onClick={handleLogin}
                className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 transition-all duration-200 transform border border-blue-200 rounded-lg md:px-4 md:py-2 md:text-base hover:bg-blue-50 hover:border-blue-300 hover:shadow-md hover:scale-105"
            >
                <LogIn className="w-4 h-4 mr-1 md:mr-2" />
                Connexion
            </button>
            <button
                onClick={handleRegister}
                className="flex items-center px-3 py-2 text-sm font-medium text-white transition-all duration-200 transform rounded-lg md:px-4 md:py-2 md:text-base bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:shadow-lg hover:scale-105"
            >
                <UserPlus className="w-4 h-4 mr-1 md:mr-2" />
                Inscription
            </button>
        </div>
    );

    // Render user profile (when logged in)
    const userConnected = JSON.parse(localStorage.getItem('user'));


    const renderUserProfile = () => (
        <div className="relative" ref={userMenuRef}>
            <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center justify-center w-8 h-8 text-white transition-all duration-200 transform rounded-full md:w-10 md:h-10 bg-gradient-to-br from-blue-500 to-purple-600 hover:shadow-lg hover:scale-105"
            >
                {/* <User className="w-4 h-4 md:w-5 md:h-5" /> */}
                <img src={`https://formafusionmg.ams3.cdn.digitaloceanspaces.com/formafusionmg/img/referents/${userConnected.photo}`} alt="" className='rounded-full' />
            </button>

            {showUserMenu && (
                <div className="absolute right-0 w-64 p-4 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl md:w-72">
                    <div className="flex items-center pb-4 space-x-3 border-b border-gray-200">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-purple-600">
                            <User className="w-5 h-5 text-white md:w-6 md:h-6" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold md:text-base">{userConnected.name}</h3>
                            <p className="text-xs text-gray-500 md:text-sm">
                                {userConnected.email}
                            </p>
                        </div>
                    </div>
                    <div className="py-2">
                        <button
                            onClick={handleLogout}
                            className="flex items-center w-full p-2 space-x-3 text-sm text-red-600 transition-colors rounded-lg hover:bg-red-50 md:text-base"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Déconnexion</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-40 border-b backdrop-blur-md bg-white/80 border-white/20">
                <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-4">
                        {/* Logo */}
                        <div className="flex items-center space-x-2 cursor-pointer group" onClick={goBackToSearch}>
                            <div className="flex items-center justify-center w-10 h-10 transition-all duration-300 rounded-xl">
                                {/* <Search className="w-6 h-6 text-white" /> */}
                                <img src="build/image/Logo_mark.svg" className='w-8 h-8' alt="" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-purple-600 bg-clip-text">
                                    Forma Fusion
                                </h1>
                            </div>
                        </div>

                        {/* Search Bar in Header (when showing results) */}
                        {showSearchResults && (
                            <div className="flex-1 max-w-2xl mx-4 md:mx-8">
                                <div className="relative" ref={searchInputRef}>
                                    <input
                                        type="text"
                                        value={searchInput}
                                        onChange={(e) => handleSearchInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="w-full h-10 pl-10 pr-10 text-sm transition-all duration-300 border-2 border-gray-200 outline-none md:h-12 md:pl-12 md:pr-12 rounded-xl md:text-base focus:border-blue-500 focus:shadow-lg bg-white/90 backdrop-blur-sm"
                                        placeholder="Rechercher..."
                                        autoComplete="off"
                                        spellCheck="false"
                                    />

                                    <div className="absolute transform -translate-y-1/2 left-3 md:left-4 top-1/2">
                                        <Search className="w-4 h-4 text-gray-400 md:w-5 md:h-5" />
                                    </div>

                                    <div className="absolute transform -translate-y-1/2 right-1 md:right-2 top-1/2">
                                        <button
                                            onClick={() => performSearch()}
                                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-1.5 md:p-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                                        >
                                            <Search className="w-3 h-3 md:w-4 md:h-4" />
                                        </button>
                                    </div>

                                    {renderSuggestions()}
                                </div>
                            </div>
                        )}

                        {/* Navigation & User Area */}
                        <div className="flex items-center space-x-3 md:space-x-6">
                            {/* Apps Menu - Only show when logged in */}
                            {isLoggedIn && (
                                <div className="relative hidden md:block" ref={appsMenuRef}>
                                    <button
                                        onClick={() => setShowAppsMenu(!showAppsMenu)}
                                        className="p-2 transition-colors rounded-full hover:bg-gray-100"
                                    >
                                        <Menu className="w-5 h-5" />
                                    </button>

                                    {showAppsMenu && (
                                        <div className="absolute right-0 p-4 mt-2 bg-gray-300 border border-gray-200 rounded-lg shadow-xl w-96">
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://apprenants.forma-fusion.com/cfp/apprenants" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Apprenants.png" />
                                                        <span className="text-xs">Apprenants</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="/sdasdasdas" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Formateurs.png" />
                                                        <span className="text-xs">Formateurs</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="/sdasdasdas" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Administrateurs.png" />
                                                        <span className="text-xs">Administrateurs</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://projets.forma-fusion.com/cfp/projets" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Projets.png" />
                                                        <span className="text-xs">Projets</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://agenda.forma-fusion.com/agendaCfps" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Agenda.png" />
                                                        <span className="text-xs">Agenda</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://reservation.forma-fusion.com/cfp/rsv/5" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Réservation.png" />
                                                        <span className="text-xs">Réservation</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://suivipeda.forma-fusion.com/cfp/peda" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Suivi pédagogique.png" />
                                                        <span className="text-xs">Suivi pédagogique</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://evaluations.forma-fusion.com/cfp/projets" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Evaluation.png" />
                                                        <span className="text-xs">Evaluation</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://tests.forma-fusion.com/qcm/index" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Tests.png" />
                                                        <span className="text-xs">Tests</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://factures.forma-fusion.com/cfp/factures/id/1" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Factures.png" />
                                                        <span className="text-xs">Factures</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://clients.forma-fusion.com/cfp/invites/etp/list/1" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Clients.png" />
                                                        <span className="text-xs">Clients</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://licence.forma-fusion.com/cfp/abonnement" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Licence.png" />
                                                        <span className="text-xs">Licence</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://marketplaces.forma-fusion.com/" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Marketplace.png" />
                                                        <span className="text-xs">Marketplace</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://photo.forma-fusion.com/cfp/gallery" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Photos.png" />
                                                        <span className="text-xs">Photos</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://catalogue.forma-fusion.com/cfp/modules" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Catalogue.png" />
                                                        <span className="text-xs">Catalogue</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://presence.forma-fusion.com/cfp/projets" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Présence.png" />
                                                        <span className="text-xs">Présence</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://dossiers.forma-fusion.com/cfp/dossier" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Dossiers.png" />
                                                        <span className="text-xs">Dossiers</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://badge.forma-fusion.com/cfp/badge" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Badges.png" />
                                                        <span className="text-xs">Badges</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://lieu-salle.forma-fusion.com/cfp/lieux" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Lieu et Salle.png" />
                                                        <span className="text-xs">Lieu et Salle</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://analytics.forma-fusion.com/home" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Analytics.png" />
                                                        <span className="text-xs">Analytics</span>
                                                    </a>
                                                </div>
                                                <div className="p-3 text-center transition-colors rounded-lg cursor-pointer hover:bg-gray-50">
                                                    <a href="https://reporting.forma-fusion.com/reporting/formation" className='flex flex-col items-center justify-center' target='_blank'>
                                                        <img alt="Apprenants Icon" class="mb-2 w-9 h-9 object-contain" loading="lazy" src="https://factures.forma-fusion.com/img/icones/Reporting.png" />
                                                        <span className="text-xs">Reporting</span>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Conditional Authentication Area */}
                            {isLoggedIn ? renderUserProfile() : renderAuthButtons()}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            {!isLoggedIn ? (
                // Login/Registration Page for non-authenticated users
                <main className="flex flex-col items-center justify-center flex-1 px-4 py-8 md:px-6 md:py-16 min-h-[80vh]">
                    <div className="w-full max-w-md p-8 bg-white border border-gray-200 shadow-xl rounded-2xl">
                        <div className="mb-8 text-center">
                            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl">
                                <Search className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="mb-2 text-2xl font-bold text-gray-900">
                                Bienvenue sur Forma Fusion
                            </h2>
                            <p className="text-gray-600">
                                Connectez-vous pour accéder à la recherche intelligente
                            </p>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={handleLogin}
                                className="flex items-center justify-center w-full px-6 py-3 text-base font-medium text-white transition-all duration-200 transform rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:shadow-lg hover:scale-105"
                            >
                                <LogIn className="w-5 h-5 mr-2" />
                                Se connecter
                            </button>

                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 text-gray-500 bg-white">ou</span>
                                </div>
                            </div>

                            <button
                                onClick={handleRegister}
                                className="flex items-center justify-center w-full px-6 py-3 text-base font-medium text-blue-600 transition-all duration-200 transform border-2 border-blue-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 hover:shadow-md hover:scale-105"
                            >
                                <UserPlus className="w-5 h-5 mr-2" />
                                Créer un compte
                            </button>
                        </div>

                        <div className="mt-8 text-center">
                            <p className="text-sm text-gray-500">
                                En vous connectant, vous acceptez nos conditions d'utilisation
                            </p>
                        </div>
                    </div>
                </main>
            ) : showSearchResults ? (
                isLoggedIn ? (
                    <SearchResults searchQuery={currentSearchQuery} />
                ) : (
                    <div className="flex items-center justify-center min-h-[60vh]">
                        <div className="text-center">
                            <h2 className="mb-4 text-2xl font-bold text-gray-900">Accès restreint</h2>
                            <p className="mb-6 text-gray-600">Veuillez vous connecter pour voir les résultats de recherche</p>
                            {renderAuthButtons()}
                        </div>
                    </div>
                )
            ) : (
                <main className="flex flex-col items-center justify-center flex-1 px-4 py-8 md:px-6 md:py-16">
                    {/* Enhanced Logo */}
                    <div className="mb-8 text-center md:mb-16">
                        <div className="relative">
                            <h1 className="mb-4 text-4xl tracking-tight sm:text-6xl md:text-7xl lg:text-9xl font-extralight md:mb-6">
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">F</span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">o</span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">r</span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">m</span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">a</span>
                                <span className="mx-2 md:mx-4"></span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">F</span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">u</span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">s</span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">i</span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">o</span>
                                <span className="inline-block text-gray-600 transition-transform duration-300 transform hover:scale-110">n</span>
                            </h1>
                            <p className="text-lg text-gray-600 md:text-xl">
                                Simplifiez votre recherche de formation
                            </p>
                        </div>
                    </div>

                    {/* Enhanced Search Bar */}
                    <div className="w-full max-w-4xl mb-8 md:mb-16">
                        <div className="relative" ref={searchInputRef}>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => handleSearchInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="w-full h-12 pl-12 pr-16 text-base transition-all duration-300 border-2 border-gray-200 outline-none md:h-16 md:pl-16 md:pr-20 rounded-2xl md:text-lg focus:border-blue-500 focus:shadow-2xl bg-white/90 backdrop-blur-sm hover:shadow-lg"
                                placeholder="Rechercher des formations, cours, certifications..."
                                autoComplete="off"
                                spellCheck="false"
                            />

                            <div className="absolute transform -translate-y-1/2 left-4 md:left-6 top-1/2">
                                <Search className="w-5 h-5 text-gray-400 md:w-6 md:h-6" />
                            </div>

                            <div className="absolute transform -translate-y-1/2 right-2 md:right-3 top-1/2">
                                <button
                                    onClick={() => performSearch()}
                                    className="p-2 text-white transition-all duration-200 transform shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 md:p-3 rounded-xl hover:from-blue-700 hover:to-purple-700 hover:shadow-xl hover:scale-105"
                                >
                                    <Search className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                            </div>

                            {/* Enhanced Suggestions */}
                            {showSuggestions && (
                                <div className="absolute left-0 right-0 z-30 mt-3 border border-gray-100 shadow-2xl top-full rounded-2xl bg-white/95 backdrop-blur-md">
                                    <div className="p-3 overflow-y-auto max-h-80">
                                        {isLoadingSuggestions ? (
                                            <div className="flex items-center justify-center p-4">
                                                <div className="w-6 h-6 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                                                <span className="ml-2 text-gray-600">Recherche en cours...</span>
                                            </div>
                                        ) : suggestions.length > 0 ? (
                                            suggestions.map(renderSuggestion)
                                        ) : (
                                            <div className="p-3 text-sm text-center text-gray-500">
                                                Aucune suggestion trouvée
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            )}
        </div>
    );
}

export default App;